import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FaFire } from 'react-icons/fa'

import Heders from '../components/Headers'
import Banner from '../components/Banner'
import TrustBar from '../components/TrustBar'
import ShopProducts from '../components/products/ShopProducts'
import DopingProductsCarousel from '../components/DopingProductsCarousel'
import HomeBlogSection from '../components/HomeBlogSection'
import Footer from '../components/Footer'
import DigitalProductCard from '../components/DigitalProductCard'
import HomeReviews from '../components/HomeReviews'

import CategoryStrip from '../components/home/CategoryStrip'
import HomeSection from '../components/home/HomeSection'
import SellerSpotlight from '../components/home/SellerSpotlight'
import MarketplaceCTA from '../components/home/MarketplaceCTA'
import BecomeSellerCTA from '../components/home/BecomeSellerCTA'
import CampaignHomeSection from '../components/home/CampaignHomeSection'
import GiveawayHomeSection from '../components/home/GiveawayHomeSection'
import OfficialTopUpSection from '../components/home/OfficialTopUpSection'
import MyGiveawaysHomeStrip from '../components/home/MyGiveawaysHomeStrip'
import WhyItemzo from '../components/home/WhyItemzo'
import HomepageCTA from '../components/home/HomepageCTA'

import { SectionHeader, Skeleton } from '../components/ui'

import { get_products, get_home_showcase_listings } from '../store/reducers/homeReducer'
import { get_digital_products_by_category } from '../store/reducers/digitalProductReducer'
import { get_daily_deals } from '../store/reducers/dopingReducer'
import { productImage } from '../utilies/imageUrl'

const SITE_URL = 'https://itemzo.net'
const HOME_URL = `${SITE_URL}/`
const HOME_TITLE = 'ItemZo - Oyun hesabları, digital məhsullar və gift card bazarı'
const HOME_DESCRIPTION = 'ItemZo-da oyun hesabları, digital məhsullar, abunəliklər, gift card elanları və satıcı mağazaları ilə təhlükəsiz alış-satış et.'
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`

const stripHtml = (value = '') => String(value)
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const toAbsoluteUrl = (value) => {
  if (!value) return DEFAULT_IMAGE
  if (/^https?:\/\//i.test(value)) return value
  return `${SITE_URL}${value.startsWith('/') ? value : `/${value}`}`
}

const serializeJsonLd = (value) => JSON.stringify(value).replace(/</g, '\\u003c')

const getProduct = (item) => item?.productId || item

const isPublicProduct = (item) => {
  const product = getProduct(item)
  if (!product?.slug || !product?.name) return false
  if (product.isDeleted === true || product.isActive === false) return false
  if (product.status && !['active', 'approved'].includes(product.status)) return false
  if (product.approvalStatus && !['active', 'approved'].includes(product.approvalStatus)) return false
  return true
}

const getProductUrl = (item) => {
  const product = getProduct(item)
  return product?.serviceType === 'digital' || product?.hasPackages || product?.thumbnail
    ? `${SITE_URL}/digital/product/${encodeURIComponent(product.slug)}`
    : `${SITE_URL}/product/details/${encodeURIComponent(product.slug)}`
}

// ── Grid skeleton for digital products rail while loading ─────
const DigitalGridSkeleton = () => (
  <div className='grid [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))] gap-6'>
    {Array.from({ length: 6 }).map((_, i) => (
      <Skeleton.Card key={i} />
    ))}
  </div>
)

const Home = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const {
    products, categorys, loader, pagination,
    homeShowcaseProducts, homeShowcasePagination, homeShowcaseLoader
  } = useSelector(state => state.home)
  const { products: digitalProducts } = useSelector(state => state.digitalProduct)
  const { dailyDeals } = useSelector(state => state.doping)

  // Phase 4K — "Önə çıxarılan elanlar" = STRICT bumped feed (type=bumped).
  // "Daha çox" hər klikdə +6.
  const normalCategories = categorys?.filter(c => !c.isDigital) || []
  const digitalCategories = categorys?.filter(c => c.isDigital) || []

  useEffect(() => {
    // get_category() is already dispatched once in App.jsx on app mount;
    // avoid the duplicate request here.
    dispatch(get_products({ limit: 12 }))
    dispatch(get_digital_products_by_category({ category: 'Mobil Oyunlar' }))
    dispatch(get_daily_deals({ page: 1, limit: 5 }))
    dispatch(get_home_showcase_listings({ page: 1, limit: 12 }))
  }, [dispatch])

  const showcaseProducts = (homeShowcaseProducts || []).map(getProduct).filter(isPublicProduct)
  const latestProducts = (products || []).map(getProduct).filter(isPublicProduct)

  const loadMoreShowcase = () => dispatch(get_home_showcase_listings({
    offset: showcaseProducts.length, limit: 6, append: true
  }))
  const loadMoreLatest = () => dispatch(get_products({
    offset: latestProducts.length, limit: 6, append: true
  }))

  // ── JSON-LD ──────────────────────────────────────────────────
  const visibleItems = [
    ...(dailyDeals || []),
    ...(showcaseProducts || []),
    ...(digitalProducts || []),
    ...(products || [])
  ].filter(isPublicProduct)

  const seenProductUrls = new Set()
  const itemListProducts = visibleItems.filter(item => {
    const url = getProductUrl(item)
    if (seenProductUrls.has(url)) return false
    seenProductUrls.add(url)
    return true
  }).slice(0, 24)

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ItemZo',
    url: HOME_URL,
    logo: DEFAULT_IMAGE
  }
  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ItemZo',
    url: HOME_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/products/search?value={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  }
  const itemListJsonLd = itemListProducts.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: itemListProducts.map((item, index) => {
      const product = getProduct(item)
      return {
        '@type': 'ListItem',
        position: index + 1,
        url: getProductUrl(item),
        item: {
          '@type': 'Product',
          name: stripHtml(product.name),
          url: getProductUrl(item),
          image: toAbsoluteUrl(product.thumbnail || productImage(product))
        }
      }
    })
  } : null

  return (
    <div className='min-h-screen w-full #fff'>
      <Helmet>
        <title>{HOME_TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={HOME_URL} />
        <meta property="og:title" content={HOME_TITLE} />
        <meta property="og:description" content={HOME_DESCRIPTION} />
        <meta property="og:image" content={DEFAULT_IMAGE} />
        <meta property="og:url" content={HOME_URL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={HOME_TITLE} />
        <meta name="twitter:description" content={HOME_DESCRIPTION} />
        <meta name="twitter:image" content={DEFAULT_IMAGE} />
        <script type="application/ld+json">
          {serializeJsonLd(organizationJsonLd)}
        </script>
        <script type="application/ld+json">
          {serializeJsonLd(websiteJsonLd)}
        </script>
        {itemListJsonLd && (
          <script type="application/ld+json">
            {serializeJsonLd(itemListJsonLd)}
          </script>
        )}
      </Helmet>
      <Heders />
      <h1 className='sr-only'>{t('home.srHeading')}</h1>

      {/* HERO — banner sistemi (admin slider + seller ödənişli promo kartlar) */}
      <Banner />

      {/* TRUST BAR */}
      <TrustBar />

      {/* CATEGORY DISCOVERY (Fiziki/Digital toggle) */}
      <CategoryStrip
        normalCategories={normalCategories}
        digitalCategories={digitalCategories}
      />

      {/* ✅ Rəsmi Top-Up və E-Pin (Itemzo rəsmi storefront) — kateqoriyadan sonra */}
      <OfficialTopUpSection />

      {/* CAMPAIGN (admin-curated) — hides when no active campaign exists */}
      <CampaignHomeSection />

      {/* DAILY DEAL */}
      {dailyDeals?.length > 0 && (
        <HomeSection card tone='danger'>
          <SectionHeader
            eyebrow={{ icon: <FaFire />, label: t('home.daily.eyebrow'), tone: 'danger' }}
            title={t('home.daily.title')}
            subtitle={t('home.daily.subtitle')}
            action={{ to: '/bazar?type=weekly', label: t('buttons.viewAll') }}
          />
          <DopingProductsCarousel
            products={dailyDeals.slice(0, 12)}
            source='daily_deal'
          />
        </HomeSection>
      )}

      {/* DIGITAL PRODUCTS */}
      {/* <HomeSection>
        <SectionHeader
          eyebrow={{ label: 'Digital', tone: 'brand' }}
          title='Digital Məhsullar'
          subtitle='Hesab, abunəlik və gift card kateqoriyasında populyar elanlar.'
          action={{ to: '/digital/Mobil Oyunlar', label: 'Hamısına bax' }}
        />
        {digitalProducts?.length > 0 ? (
          <div className='grid [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))] gap-6 sm:gap-4'>
            {digitalProducts.slice(0, 12).map((product, i) => (
              <DigitalProductCard key={product._id || i} product={product} />
            ))}
          </div>
        ) : (
          <DigitalGridSkeleton />
        )}
      </HomeSection> */}

      {showcaseProducts.length > 0 && <HomeSection>
        <SectionHeader
          eyebrow={{ label: 'Vitrin', tone: 'brand' }}
          title='Ana səhifə vitrini'
          subtitle='Premium mövqedə seçilmiş elanlar'
        />
        <ShopProducts products={showcaseProducts} styles='grid' source='home_showcase' gridClassName='grid-cols-6 xl:grid-cols-5 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2' />
        {homeShowcasePagination?.hasNextPage && (
          <div className='mt-6 text-center'>
            <button type='button' disabled={homeShowcaseLoader} onClick={loadMoreShowcase} className='rounded-xl border border-violet-200 bg-white px-5 py-2.5 text-sm font-bold text-violet-700 shadow-sm transition-colors hover:border-violet-500 hover:bg-violet-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-60'>
              {homeShowcaseLoader ? 'Yüklənir…' : 'Daha çox vitrin elanı göstər'}
            </button>
          </div>
        )}
      </HomeSection>}

      <HomeSection>
        <SectionHeader
          eyebrow={{ label: t('home.newListings.eyebrow'), tone: 'brand' }}
          title='Ən son elanlar'
          subtitle='Yeni əlavə edilən məhsullar'
          action={{ to: '/bazar', label: 'Bütün elanlara bax' }}
        />
        {latestProducts.length > 0 ? <>
          <ShopProducts products={latestProducts} styles='grid' source='organic' gridClassName='grid-cols-6 xl:grid-cols-5 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2' />
          {pagination?.hasNextPage && (
            <div className='mt-6 text-center'>
              <button type='button' disabled={loader} onClick={loadMoreLatest} className='rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition hover:border-brand-400 hover:text-brand-700 disabled:opacity-60'>
                {loader ? 'Yüklənir…' : 'Daha çox yeni elan göstər'}
              </button>
            </div>
          )}
        </> : (
          <div className='grid [grid-template-columns:repeat(auto-fill,minmax(210px,1fr))] gap-5'>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton.Card key={i} />
            ))}
          </div>
        )}
      </HomeSection>

      {/* SELLER SPOTLIGHT (public /sellers/active) */}
      <SellerSpotlight />

      {/* PLATFORM REVIEWS */}

      {/* BLOG */}
      <HomeBlogSection />

      {/* PERSONAL GIVEAWAY RAIL — only renders for logged-in customers who
          have joined at least one giveaway; component returns null otherwise. */}
      <MyGiveawaysHomeStrip />

      {/* ACTIVE GIVEAWAYS — hides when none */}
      <GiveawayHomeSection />

      {/* WHY ITEMZO — etibar + dəyər təklifi */}
      <WhyItemzo />

      {/* COMPACT SELLER CTA → /seller-hub (panel demo) + /satici-ol (apply).
          Replaced the large SellerStoreSimulator section. */}
      <BecomeSellerCTA />

      {/* BOTTOM MARKETPLACE CTA */}
      <MarketplaceCTA />
      <HomeReviews />

      {/* ADMIN-İDARƏLİ BÖYÜK CTA (Rəqəmsal Ticarətə Başla) — söndürülübsə gizli */}
      {/* <HomepageCTA /> */}

      <Footer />
    </div>
  )
}

export default Home
